import _ from 'lodash';
import Logger from '../util/Logger';
import { AuthMethod, authMethodFromString } from './AuthMethod';
import DateTime from './DateTime';

/**
 * Methods for consistent access to User properties via server returned JSON.
 *
 * All interactions or accesses on User fields should be handled via helper method in this
 * class. Methods should only be to avoid `this` scoping issues in nested calls.
 *
 * Utility methods should throw in the case of unexpected constant mismatch such as missing data,
 * all calls to these methods should be guarded at the callsite.
 */
export default {

  /**
   * Extracts the id field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {string} id of the tenant
   */
  getId (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getId() without a Tenant object');
    } else {
      const id = _.get(tenant, 'id');
      if (!id) {
        Logger.assert('Tenant was missing an id');
      } else {
        return id;
      }
    }
  },

  /**
   * Extracts the name field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {string} name of the tenant
   */
  getName (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getName() without a Tenant object');
    } else {
      const name = _.get(tenant, 'name');
      if (!name) {
        Logger.error('Tenant was missing a name', this.getId(tenant));
        return '';
      } else {
        return name;
      }
    }
  },

  /**
   * Extracts the earliest activity time
   *
   * @param {object} tenant Tenant json
   * @returns {moment.Moment} Earliest activity time
   */
  getEarliestActivityTime (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getEarliestActivityTime() without a Tenant object');
    } else {
      const time = _.get(tenant, 'earliest_activity_time');
      if (!time) {
        return null;
      } else {
        return DateTime.unixSecondsToMoment(time);
      }
    }
  },

  /**
   * Extracts the domains field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {Array<string>} list of tenant domain names
   */
  getDomains (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getDomains() without a Tenant object');
    } else {
      const domains = _.get(tenant, 'domains');
      if (!domains) {
        Logger.error('Tenant was missing domain list', this.getId(tenant));
        return [];
      } else {
        return domains;
      }
    }
  },

  /**
   * Extracts the tenant owner mini object
   *
   * @param {object} tenant Tenant json
   * @returns {object} Tenant owner
   */
  getOwner (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getOwner() without a Tenant object');
    } else {
      return _.get(tenant, 'owner') || null;
    }
  },

  /**
   * Extracts the short_share_link field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is short share link enabled
   */
  isShortShareLink (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isShortShareLink() without a Tenant object');
    } else {
      const share = _.get(tenant, 'short_share_link');
      if (share === null || share === undefined) {
        Logger.error('Tenant was missing short_share_link setting', this.getId(tenant));
        return false;
      } else {
        return !!share;
      }
    }
  },

  /**
   * Get SAML verify URL
   *
   * @param {object} tenant Tenant json
   * @returns {string} saml verify url
   */
  getSamlVerifyUrl (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isSamlVerified() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_verify_url') || '';
    }
  },

  /**
   * Is tenant org_dashboard_enabled
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is org dashboard enabled
   */
  isOrgDashboardEnabled (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isOrgDashboardEnabled() without a Tenant object');
    } else {
      return !!_.get(tenant, 'org_dashboard_enabled');
    }
  },

  /**
   * Extracts the trial field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is Trial
   */
  isTrial (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isTrial() without a Tenant object');
    } else {
      const trial = _.get(tenant, 'trial');
      if (trial === null || trial === undefined) {
        Logger.error('Tenant was missing trial setting', this.getId(tenant));
        return true;
      } else {
        return !!trial;
      }
    }
  },

  /**
   * Extracts the test field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is test
   */
  isTest (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isTest() without a Tenant object');
    } else {
      const test = _.get(tenant, 'test');
      if (test === null || test === undefined) {
        Logger.error('Tenant was missing test setting', this.getId(tenant));
        return false;
      } else {
        return !!test;
      }
    }
  },

  /**
   * Extracts the media_allowed field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is image attachment allowed
   */
  isImagesAllowed (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isImages() without a Tenant object');
    } else {
      const allowed = _.get(tenant, 'answer_media');
      if (allowed === null || allowed === undefined) {
        Logger.error('Tenant was missing answer_media setting', this.getId(tenant));
        return false;
      } else {
        return !!allowed;
      }
    }
  },

  /**
   * Extracts the media_attachment_allowed field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is pdf attachment allowed
   */
  isPdfsAllowed (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isPdfsAllowed() without a Tenant object');
    } else {
      const allowed = _.get(tenant, 'answer_media_attachments');
      if (allowed === null || allowed === undefined) {
        Logger.error('Tenant was missing answer_media_attachments setting', this.getId(tenant));
        return false;
      } else {
        return !!allowed;
      }
    }
  },

  /**
   * Extracts the restricted_self_launch field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is restricted self launch
   */
  isRestrictedSelfLaunch (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isRestrictedSelfLaunch() without a Tenant object');
    } else {
      const restricted = _.get(tenant, 'restrict_self_launch');
      if (restricted === null || restricted === undefined) {
        Logger.error('Tenant was missing restrict_self_launch setting', this.getId(tenant));
        return false;
      } else {
        return !!restricted;
      }
    }
  },

  /**
   * Extracts the show_code_of_conduct field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {boolean} Is show code of conduct
   */
  isShowCodeOfConduct (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call isShowCodeOfConduct() without a Tenant object');
    } else {
      const showCodeOfConduct = _.get(tenant, 'show_code_of_conduct');
      if (showCodeOfConduct === null || showCodeOfConduct === undefined) {
        Logger.error('Tenant was missing show_code_of_conduct setting', this.getId(tenant));
        return false;
      } else {
        return !!showCodeOfConduct;
      }
    }
  },

  /**
   * Extracts the authentication_method field from a given tenant json
   *
   * @param {object} tenant Tenant json
   * @returns {string} Tenant authentication method
   */
  getAuthMethod (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getAuthMethod() without a Tenant object');
    } else {
      const auth = _.get(tenant, 'authentication_strategy');
      if (!auth) {
        Logger.error('Tenant was missing authentication_strategy setting', this.getId(tenant));
        return AuthMethod.DEFAULT;
      } else {
        return authMethodFromString(auth);
      }
    }
  },

  /**
   * Get SAML IDP metadata url
   *
   * @param {object} tenant Tenant json
   * @returns {string} URL
   */
  getSamlIdpMetadataUrl (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlIdpMetadataUrl() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_idp_metadata_url') || '';
    }
  },

  /**
   * Get SAML IDP XML metadata
   *
   * @param {object} tenant Tenant json
   * @returns {string} XML
   */
  getSamlIdpXmlMetadata (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlIdpXmlMetadata() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_idp_xml_metadata') || '';
    }
  },

  /**
   * Get SAML SP XML metadata
   *
   * @param {object} tenant Tenant json
   * @returns {string} XML - can be empty if not set up
   */
  getSamlSpXmlMetadata (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlSpXmlMetadata() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_sp_xml_metadata') || '';
    }
  },

  /**
   * Get SAML SP SSO url
   *
   * @param {object} tenant Tenant json
   * @returns {string} url - can be empty if not set up
   */
  getSamlSpSsoUrl (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlSpSsoUrl() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_sp_sso_url') || '';
    }
  },

  /**
   * Get SAML SP ACS url
   *
   * @param {object} tenant Tenant json
   * @returns {string} url - can be empty if not set up
   */
  getSamlSpAcsUrl (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlSpAcsUrl() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_sp_acs_url') || '';
    }
  },

  /**
   * Get SAML SP name id format
   *
   * @param {object} tenant Tenant json
   * @returns {string} format - can be empty if not set up
   */
  getSamlSpNameIdFormat (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlSpNameIdFormat() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_sp_name_id_format') || '';
    }
  },

  /**
   * Get SAML SP Attributes
   *
   * @param {object} tenant Tenant json
   * @returns {Array<string>} attributes - can be empty if not set up
   */
  getSamlSpRequiredUserAttributes (tenant) {
    if (!tenant) {
      Logger.assert('Cannot call getSamlSpRequiredUserAttributes() without a Tenant object');
    } else {
      return _.get(tenant, 'saml_sp_required_user_attributes') || [];
    }
  }
};
